import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { formatInt, localeForLanguage } from "../customerDashboardStore"
import type { DashboardWidgetProps } from "../customerWidgetRegistry"
import { requestActionPath } from "./requestActionPath"
import { RequestTopRow } from "./RequestTopRow"

// Only 3 metric kinds ever reach this widget — verified against backend
// Task 4 (mc_customer_dashboard_repository.get_waiting_top's CASE on
// metric_kind): review_ready+gap -> 'gap_questions', review_ready without a
// gap -> 'answers', everything else -> 'articles'. There is no 'conflicts'
// kind (McQuestionnaireItem.status has no conflict marker). Unrecognized
// values still render (falling back to the 'articles' label) rather than
// disappearing, since the field is a plain string, not a closed union.
const METRIC_LABEL_KEYS: Record<string, TranslationKeys> = {
  answers: "MC_CSD_ROW_ANSWERS",
  gap_questions: "MC_CSD_ROW_GAP_QUESTIONS",
  articles: "MC_CSD_ROW_ARTICLES",
}

// The backend's `step` CASE (get_waiting_top) emits exactly 7 values, but
// only 5 action-label keys exist in-register (Review/Reconcile/Confirm/Close
// gaps/Send — Task 8's full MC_CSD_ROW_* set). Two steps deliberately reuse
// another step's label rather than adding a new key:
//  - 'mapping' (processing_status = 'mapping_ready') reuses CONFIRM: the
//    pending action is confirming the column mapping — same verb as
//    confirming a freshly received request, and there is no dedicated
//    "confirm mapping" key at this list-row grain (MappingStep's own screen
//    uses MC_QP_MAP_CONFIRM_BUTTON, a longer button-specific string).
//  - 'failed' (status='extraction_failed' or any failed_* processing_status)
//    reuses REVIEW: there is no in-register retry/fix action, and opening
//    the record to review what failed is the closest available treatment.
const STEP_ACTION_KEYS: Record<string, TranslationKeys> = {
  confirm: "MC_CSD_ROW_CONFIRM",
  mapping: "MC_CSD_ROW_CONFIRM",
  reconcile: "MC_CSD_ROW_RECONCILE",
  gaps: "MC_CSD_ROW_CLOSE_GAPS",
  review: "MC_CSD_ROW_REVIEW",
  send: "MC_CSD_ROW_SEND",
  failed: "MC_CSD_ROW_REVIEW",
}

export function WaitingWidget({ stats, loadFailed }: DashboardWidgetProps) {
  const { translate, currentLanguage } = useTranslation()
  const locale = localeForLanguage(currentLanguage)
  if (stats.waitingTop.length === 0) {
    // After a failed load the list is unknown, not empty: no "nothing here".
    if (loadFailed) return null
    return (
      <div className="py-6 text-sm text-grey5">
        {translate("MC_SSD_EMPTY_LIST")}
      </div>
    )
  }
  return (
    <div>
      {stats.waitingTop.map((item) => {
        // The row's actual CTA — needs_confirm/extraction_failed rows have
        // no record to review yet, so this routes to the confirm/import flow
        // instead (see requestActionPath's docstring).
        const actionTo = requestActionPath(item)
        // Same key drives both the subtitle's step description and the row's
        // CTA — no separate step-noun copy exists in-register, and inventing
        // one would duplicate the action's own meaning for no benefit.
        const actionKey = STEP_ACTION_KEYS[item.step] ?? "MC_CSD_ROW_REVIEW"
        return (
          <RequestTopRow
            key={item.questionnaireId}
            item={item}
            subtitleTail={translate(actionKey)}
            metric={
              // Overdue rows tint the prominent metric value red — mirrors
              // the supplier OutstandingWidget's overdue treatment.
              <span className={cn(item.overdue && "text-redLight")}>
                {formatInt(item.metric, locale)}
              </span>
            }
            metricLabel={translate(
              METRIC_LABEL_KEYS[item.metricKind] ?? "MC_CSD_ROW_ARTICLES",
            )}
            action={{ label: translate(actionKey), to: actionTo }}
          />
        )
      })}
    </div>
  )
}
